"use client";
import { useSession } from "@/component/SessionProvider";
import { useEffect } from "react";

import { playNotificationSound } from "./playNotification";
import { toast } from "react-toastify";
import { NotificationData } from "@/app/api/address-webhook/interface";
import { socket } from "./socketSingleton";
import {
  getUnreadTransactionNotifications,
  markManyTransactionNotificationsAsRead,
  markTransactionNotificationAsRead,
} from "@/actions/transaction/methods";
import { isActionError } from "@/utils/isActionError";

console.log(
  "🔌 Connecting to WebSocket Server:",
  process.env.NEXT_PUBLIC_WEB_SOCKET_URI
);

export default function WebSocketClient() {
  const { session, pushSuccessfulTransaction } = useSession();
  const userId = session.user?.id;
  const isAdmin = session.user?.isAdmin;

  useEffect(() => {
    if (!userId) return;

    // 💡 Define the event handler separately
    const onTransactionUpdate = (event: NotificationData) => {
      const data = event?.data;
      console.log("🔔 New event:", event, { data });

      const txId = data?.txId;
      if (txId) {
        pushSuccessfulTransaction?.(data);

        markTransactionNotificationAsRead({
          userId,
          transactionId: txId,
        });
      }

      if (event?.message) {
        playNotificationSound();
        toast(event.message);
      }
    };

    const onConnect = () => {
      getUnreadTransactionNotifications(userId).then((notifications) => {
        if (isActionError(notifications)) {
          console.error("Error fetching unread notifications:", notifications);
          return;
        }

        const seenTxIds: string[] = [];
        notifications.forEach((notification) => {
          const _data = notification.data || "{}";
          console.log("🔔 New event:", notification);

          if (_data && notification.transactionId) {
            const data = JSON.parse(_data);

            pushSuccessfulTransaction?.(data);
            seenTxIds.push(notification.transactionId);

            if (notification?.message) {
              playNotificationSound();
              toast(notification.message);
            }
          }
        });

        if (seenTxIds.length) {
          markManyTransactionNotificationsAsRead({
            userId,
            transactionIds: seenTxIds,
          });
        }
      });
    };

    const onDisconnect = () => {
      console.log("⚡ Disconnected from WebSocket server");
    };

    const subscribeUser = () => {
      if (!userId || document.visibilityState !== "visible") return;
      if (!socket.connected) {
        socket.connect();
      }

      console.log("🔌 Registering user:", userId);
      socket.emit("registerUser", userId);
    };

    // ✅ Attach listeners
    socket.on("transactionUpdate", onTransactionUpdate);

    if (isAdmin) {
      socket.on("connect", onConnect);
      socket.on("disconnect", onDisconnect);
    }

    subscribeUser();
    document.addEventListener("visibilitychange", subscribeUser);

    return () => {
      // ✅ Remove the listener on cleanup
      socket.off("transactionUpdate", onTransactionUpdate);
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);

      document.removeEventListener("visibilitychange", subscribeUser);
    };
  }, [userId]);

  return null;
}
